import type { MealItem, MeasureUnit, SupermarketSection } from '@cadencia/contracts';
import { SECTION_ORDER } from '@cadencia/contracts';
import { requireFood } from '../catalog/foods.js';
import { roundTo } from '../units/round.js';

/**
 * Lista de la compra (docs/NUTRITION_ENGINE.md §12.1, docs/PRODUCT_SPEC.md §9).
 *
 * El algoritmo del §12.1 en seis pasos: recopilar, agregar, normalizar
 * unidades, restar despensa, convertir a formato comercial y agrupar por
 * seccion.
 *
 * La regla que gobierna todo el modulo: **`needed` y `purchase` no se mezclan
 * nunca** (requisito §30 del prompt). Lo que pide la nutricion y lo que hay que
 * echar al carro son dos numeros distintos, y el segundo no sustituye al
 * primero. Un modulo que devolviera solo «compra 1 kg» hace imposible saber
 * despues si sobraron 130 g o si es que se compro de mas.
 */

/** Lo que hay en casa, ya sin las columnas de sincronizacion. */
export interface PantryStock {
  foodSlug: string;
  quantity: number;
  unit: MeasureUnit;
}

export interface ShoppingLine {
  foodSlug: string;
  nameEs: string;
  section: SupermarketSection;
  /** Lo que pide la nutricion, antes de mirar la despensa. */
  neededAmount: number;
  neededUnit: MeasureUnit;
  pantryDeducted: number;
  /** Lo que hay que comprar, ya en formato comercial. */
  purchaseAmount: number;
  purchaseUnit: MeasureUnit;
  packSize: number | null;
  packCount: number | null;
  reasonCodes: string[];
}

/**
 * Redondeo al alza cuando el alimento no declara formato comercial (§12.1
 * paso 5, «redondeo al alza a 50 g / 100 g / 1 unidad segun categoria»).
 *
 * El documento no dice QUE categoria lleva cada paso, asi que se decide aqui y
 * se documenta: lo que se vende a peso en el mostrador admite pasos de 50 g,
 * porque la bascula del super los da; lo envasado va de 100 en 100, que es como
 * vienen las bolsas. Es una convencion de presentacion, no un calculo
 * nutricional: `needed_amount` no se toca.
 */
export const PURCHASE_ROUNDING = {
  unit: 1,
  loose: 50,
  packaged: 100,
} as const;

/** Secciones que se venden a peso, no en envase cerrado. */
export const LOOSE_SECTIONS: readonly SupermarketSection[] = ['produce', 'meat', 'fish'];

/**
 * Clave de agregacion: alimento **y unidad**.
 *
 * No basta el alimento. Un huevo sin `unitWeightG` puede aparecer en gramos en
 * una receta y en unidades en otra, y sumar «3» con «150» daria 153 de nada.
 * El §3.9 de TEST_STRATEGY.md pide dos lineas separadas y marcadas, y por eso
 * el indice unico de la tabla incluye tambien `needed_unit`.
 */
function clave(foodSlug: string, unit: MeasureUnit): string {
  return `${foodSlug}|${unit}`;
}

/**
 * Pasa una cantidad a la unidad en la que se agrega ese alimento.
 *
 * Una unidad se convierte a gramos si el catalogo dice cuanto pesa. Si no lo
 * dice, se queda en unidades **y se marca**: inventar el peso de «una manzana»
 * seria meter un numero falso en la lista y no poder distinguirlo despues de
 * uno medido.
 *
 * Gramos y mililitros no se mezclan jamas. El §12.1 lo permite «solo si el
 * alimento define densidad», y el catalogo no tiene densidad: cada alimento
 * declara una `unitBase` y punto.
 */
function normalizar(
  foodSlug: string,
  amount: number,
  unit: MeasureUnit,
): { amount: number; unit: MeasureUnit; reason: string | null } {
  const food = requireFood(foodSlug);
  if (unit !== 'unit') return { amount, unit, reason: null };
  if (food.unitWeightG !== null && food.unitBase === 'g') {
    return { amount: amount * food.unitWeightG, unit: 'g', reason: null };
  }
  return { amount, unit: 'unit', reason: 'unit_weight_unknown' };
}

/** Paso 5: de lo que falta a lo que se compra. */
function aFormatoComercial(
  foodSlug: string,
  faltan: number,
  unit: MeasureUnit,
): { purchaseAmount: number; packSize: number | null; packCount: number | null; reason: string } {
  const food = requireFood(foodSlug);

  if (faltan <= 0) {
    return { purchaseAmount: 0, packSize: null, packCount: 0, reason: 'covered_by_pantry' };
  }

  if (unit !== 'unit' && food.commercialPackG !== null) {
    const packCount = Math.ceil(faltan / food.commercialPackG);
    return {
      purchaseAmount: roundTo(packCount * food.commercialPackG, 1),
      packSize: food.commercialPackG,
      packCount,
      reason: 'commercial_pack',
    };
  }

  const paso =
    unit === 'unit'
      ? PURCHASE_ROUNDING.unit
      : LOOSE_SECTIONS.includes(food.section)
        ? PURCHASE_ROUNDING.loose
        : PURCHASE_ROUNDING.packaged;

  return {
    purchaseAmount: roundTo(Math.ceil(faltan / paso) * paso, 1),
    packSize: null,
    packCount: null,
    reason: 'rounded_up',
  };
}

export interface ShoppingListInput {
  /** Todos los items de las comidas planificadas del rango. */
  items: readonly MealItem[];
  pantry: readonly PantryStock[];
}

export interface ShoppingListResult {
  lines: ShoppingLine[];
  /** Existencias usadas, por `foodSlug`. Es el `pantry_snapshot` que se guarda. */
  pantrySnapshot: Record<string, number>;
}

/**
 * Orden de la lista: seccion primero, nombre despues.
 *
 * El orden de las secciones es el del RECORRIDO del supermercado, no el
 * alfabetico del enum: se entra por la fruta y se sale por los congelados.
 *
 * Se exporta porque lo usan dos sitios —al generar la lista y al releerla de la
 * base local— y con dos comparadores distintos la lista se reordenaria sola
 * entre una pantalla y la siguiente. No se guarda como columna de orden por lo
 * mismo: es una funcion pura de la seccion y el nombre, y una columna quedaria
 * obsoleta en cuanto cambiara cualquiera de los dos.
 */
export function compareShoppingLines(
  a: { section: SupermarketSection; nameEs: string; neededUnit: MeasureUnit },
  b: { section: SupermarketSection; nameEs: string; neededUnit: MeasureUnit },
): number {
  const seccion = SECTION_ORDER.indexOf(a.section) - SECTION_ORDER.indexOf(b.section);
  if (seccion !== 0) return seccion;
  const nombre = a.nameEs.localeCompare(b.nameEs, 'es');
  if (nombre !== 0) return nombre;
  // Dos lineas del mismo alimento en unidades distintas: orden estable.
  return a.neededUnit.localeCompare(b.neededUnit);
}

/**
 * Los seis pasos del §12.1, en orden.
 *
 * Determinista: con los mismos items y la misma despensa devuelve exactamente
 * la misma lista, en el mismo orden.
 */
export function buildShoppingList(input: ShoppingListInput): ShoppingListResult {
  // Pasos 1-3: recopilar, agregar y normalizar unidades.
  const necesario = new Map<
    string,
    { foodSlug: string; amount: number; unit: MeasureUnit; reasons: Set<string> }
  >();

  for (const item of input.items) {
    if (item.amount <= 0) continue;
    const norm = normalizar(item.foodSlug, item.amount, item.unit);
    const k = clave(item.foodSlug, norm.unit);
    const previo = necesario.get(k);
    if (previo === undefined) {
      necesario.set(k, {
        foodSlug: item.foodSlug,
        amount: norm.amount,
        unit: norm.unit,
        reasons: new Set(norm.reason === null ? [] : [norm.reason]),
      });
    } else {
      previo.amount += norm.amount;
      if (norm.reason !== null) previo.reasons.add(norm.reason);
    }
  }

  // La despensa se normaliza igual, o restaria peras de manzanas.
  const enCasa = new Map<string, number>();
  const porAlimento = new Map<string, number>();
  for (const stock of input.pantry) {
    if (stock.quantity <= 0) continue;
    const norm = normalizar(stock.foodSlug, stock.quantity, stock.unit);
    const k = clave(stock.foodSlug, norm.unit);
    enCasa.set(k, (enCasa.get(k) ?? 0) + norm.amount);
    porAlimento.set(stock.foodSlug, (porAlimento.get(stock.foodSlug) ?? 0) + norm.amount);
  }

  const pantrySnapshot: Record<string, number> = {};
  const lines: ShoppingLine[] = [];

  for (const entrada of necesario.values()) {
    const food = requireFood(entrada.foodSlug);
    const k = clave(entrada.foodSlug, entrada.unit);
    const necesita = roundTo(entrada.amount, 1);

    // Paso 4: restar despensa, pero solo la que esta en la MISMA unidad.
    const disponible = enCasa.get(k) ?? 0;
    const restado = roundTo(Math.min(disponible, necesita), 1);
    const faltan = roundTo(Math.max(0, necesita - restado), 1);

    const reasons = [...entrada.reasons];
    // Hay existencias de este alimento, pero en una unidad que no case: se
    // dice, en vez de restar a ojo o de callar que no se ha restado.
    if (disponible === 0 && (porAlimento.get(entrada.foodSlug) ?? 0) > 0) {
      reasons.push('pantry_unit_mismatch');
    }
    if (restado > 0) {
      reasons.push('pantry_deducted');
      pantrySnapshot[entrada.foodSlug] = roundTo(porAlimento.get(entrada.foodSlug) ?? 0, 1);
    }

    const compra = aFormatoComercial(entrada.foodSlug, faltan, entrada.unit);
    reasons.push(compra.reason);

    lines.push({
      foodSlug: entrada.foodSlug,
      nameEs: food.nameEs,
      section: food.section,
      neededAmount: necesita,
      neededUnit: entrada.unit,
      pantryDeducted: restado,
      purchaseAmount: compra.purchaseAmount,
      purchaseUnit: entrada.unit,
      packSize: compra.packSize,
      packCount: compra.packCount,
      reasonCodes: reasons,
    });
  }

  // Paso 6: agrupar por seccion y ordenar.
  lines.sort(compareShoppingLines);

  return { lines, pantrySnapshot };
}

export interface SectionGroup<T> {
  section: SupermarketSection;
  lines: T[];
}

/**
 * Agrupa una lista YA ORDENADA, para pintarla por secciones.
 *
 * Generica en la linea porque la pantalla y el export no manejan el mismo tipo:
 * la lista guardada trae ademas `isChecked`, y agrupar no puede perderlo por el
 * camino.
 */
export function groupBySection<T extends { section: SupermarketSection }>(
  lines: readonly T[],
): SectionGroup<T>[] {
  const grupos: SectionGroup<T>[] = [];
  for (const line of lines) {
    const ultimo = grupos[grupos.length - 1];
    if (ultimo !== undefined && ultimo.section === line.section) ultimo.lines.push(line);
    else grupos.push({ section: line.section, lines: [line] });
  }
  return grupos;
}

/** Progreso de la compra: «12 de 46 items · 26 %» (§12.3). */
export function shoppingProgress(items: readonly { isChecked: boolean }[]): {
  checked: number;
  total: number;
  pct: number;
} {
  const total = items.length;
  const checked = items.filter((item) => item.isChecked).length;
  return { checked, total, pct: total === 0 ? 0 : Math.round((checked / total) * 100) };
}
